import { useEffect } from 'react';
import { getAgreementType } from '@/types';
import type { BenchmarkRate, TradeType } from '@/types';
import type { TradeForm } from '../../components/trade-form-sections/types';
import { getDefaultBenchmarkByCurrency } from './benchmark-defaults';
import { makeCreateTradeFormDefaults } from './default-values';

/**
 * Handle the rate-entry-mode switch: Floating Fee is unsupported, so fee mode
 * forces rate_type='fixed', clears spread_bps and auto-selects the currency
 * benchmark; rebate mode clears fee_percent. It deliberately does NOT write
 * benchmark_rate — useBenchmarkRateSync does, off benchmark_name, so a late
 * registry fetch can't reset it. Extracted from useCreateTradeForm (LR-495).
 *
 * LR-631: the currency benchmark is only a PLACEHOLDER in fee mode now — the
 * moment a cash pool is selected `useCashPoolRateSync` overwrites both fields
 * with the pool's own definition, which is what the cost leg actually accrues
 * at. It still runs so a fee trade is never left without a benchmark at all.
 */
export function useRateModeReset({
  form,
  rateEntryMode,
  securityCurrency,
  hasCashPool,
}: {
  form: TradeForm;
  rateEntryMode: string | undefined;
  securityCurrency: string;
  hasCashPool: boolean;
}) {
  useEffect(() => {
    if (rateEntryMode === 'fee') {
      // Floating Fee is not a supported combination — force rate_type to 'fixed' in fee mode
      form.setFieldValue('rate_type', 'fixed');
      form.setFieldValue('spread_bps', undefined);
      // Auto-select benchmark based on currency — the PLACEHOLDER only, and only
      // while no pool owns the field. This effect also re-fires on a security
      // CURRENCY change, so without the guard picking a EUR pool (ESTR) and then
      // switching the security to a USD one would put `sofr` back in the name
      // beside the ESTR-derived rate: `useCashPoolRateSync` sees no dependency
      // move and does not reclaim it (LR-631).
      if (hasCashPool) return;
      const defaultBenchmark = getDefaultBenchmarkByCurrency(securityCurrency);
      form.setFieldValue('benchmark_name', defaultBenchmark);
      // benchmark_rate is populated by useBenchmarkRateSync (LR-435) off
      // benchmark_name — not here, so a late benchmarks fetch can't reset it.
    } else {
      // rebate mode
      form.setFieldValue('fee_percent', undefined);
    }
  }, [rateEntryMode, securityCurrency, hasCashPool, form]);
}

/**
 * LR-617: fee entry mode is a GMSLA concept — Repo / Reverse Repo (GMRA)
 * trades always book as Rebate. An effect (not a select onChange) because what
 * makes fee invalid is a trade-type change; writing 'rebate' lets
 * useRateModeReset's rebate branch clear fee_percent on the cascaded render.
 */
export function useRepoRateModeGuard({
  form,
  tradeType,
  rateEntryMode,
}: {
  form: TradeForm;
  tradeType: string | undefined;
  rateEntryMode: string | undefined;
}) {
  useEffect(() => {
    if (rateEntryMode === 'fee' && (tradeType === 'repo' || tradeType === 'reverse_repo')) {
      form.setFieldValue('rate_entry_mode', 'rebate');
    }
  }, [tradeType, rateEntryMode, form]);
}

/**
 * LR-844: a repo is a sale against cash — Repo / Reverse Repo take Cash
 * collateral only. An effect for the same reason as useRepoRateModeGuard: the
 * collateral turns invalid on a trade-type change. The Non-cash Collateral
 * Details go back to their defaults with the type — the create payload sends
 * them whatever the collateral type, so a value typed for the abandoned type
 * would otherwise ride along on the next lend booked.
 */
export function useRepoCollateralGuard({
  form,
  tradeType,
  collateralType,
}: {
  form: TradeForm;
  tradeType: string | undefined;
  collateralType: string | undefined;
}) {
  useEffect(() => {
    if (tradeType !== 'repo' && tradeType !== 'reverse_repo') return;
    if (collateralType === 'cash') return;
    const defaults = makeCreateTradeFormDefaults();
    form.setFieldValue('collateral_type', 'cash');
    form.setFieldValue('collateral_isin', defaults.collateral_isin);
    form.setFieldValue('collateral_symbol_id', defaults.collateral_symbol_id);
    form.setFieldValue('collateral_quantity', defaults.collateral_quantity);
    form.setFieldValue('collateral_cob_price', defaults.collateral_cob_price);
    form.setFieldValue('collateral_current_market_price', defaults.collateral_current_market_price);
    form.setFieldValue('collateral_percent', defaults.collateral_percent);
    form.setFieldValue('pre_delivery_party', defaults.pre_delivery_party);
    form.setFieldValue('collateral_settlement_place', defaults.collateral_settlement_place);
  }, [tradeType, collateralType, form]);
}

/**
 * LR-435: benchmark percentages arrive asynchronously from the registry and may
 * change after the broad reset effects have already run. Keep benchmark_rate in
 * sync with the selected benchmark whenever the rates load or the benchmark
 * changes — EXCEPT in rebate-fixed mode, where benchmark_rate holds the rate the
 * operator typed (see totalRate) and must not be clobbered.
 *
 * MUST stay a SEPARATE effect from useRateModeReset: it has to re-fire on the
 * async `benchmarkRates` load on its own, and it reads benchmark_name written by
 * the inventory-defaults / fee-switch effects on a later render (two-render
 * cascade). Merging them would write benchmark_rate=0 before the registry
 * resolves. Extracted from useCreateTradeForm (LR-495).
 */
export function useBenchmarkRateSync({
  form,
  benchmarkName,
  benchmarkRates,
  rateEntryMode,
  rateType,
  hasCashPool,
}: {
  form: TradeForm;
  benchmarkName: string | undefined;
  benchmarkRates: Record<string, number>;
  rateEntryMode: string | undefined;
  rateType: string | undefined;
  hasCashPool: boolean;
}) {
  useEffect(() => {
    // benchmark_name is undefined until a benchmark is chosen (on inventory
    // selection or fee-mode switch), so this stays a no-op until then.
    if (!benchmarkName) return;
    if (rateEntryMode === 'rebate' && rateType === 'fixed') return;
    // LR-631: a pooled fee trade's benchmark is the POOL's rate (fixing plus the
    // pool's spread, or a flat fixed rate). Writing the bare registry value here
    // would drop the spread and preview a cost the accrual never charges — and
    // it would fight `useCashPoolRateSync` render for render.
    if (hasCashPool && rateEntryMode === 'fee') return;
    form.setFieldValue('benchmark_rate', (benchmarkRates[benchmarkName as BenchmarkRate] ?? 0).toString());
  }, [benchmarkName, benchmarkRates, rateEntryMode, rateType, hasCashPool, form]);
}

/**
 * LR-631: GMSLA books two shapes and only one of them pools its cash — fee
 * always carries a pool, rebate carries none ("поле кэшпула вообще можно
 * убирать", 2026-08-24). The field is hidden in rebate mode, so this drops the
 * value behind it; without that a trader who picks a pool and then switches to
 * Rebate posts a pool the API rejects, pointing at a field no longer on screen.
 *
 * GMRA is left alone: a repo books rebate-only (LR-617) and its pool stays
 * mandatory, so the guard keys on the agreement family the same way
 * `CashPoolField` does rather than on the mode alone.
 */
export function useCashPoolRebateGuard({
  form,
  poolApplies,
  cashPool,
  tradeType,
}: {
  form: TradeForm;
  /** Does this trade still pool its cash? (mode AND collateral type.) */
  poolApplies: boolean;
  cashPool: number | null | undefined;
  tradeType: string | undefined;
}) {
  useEffect(() => {
    if (poolApplies || cashPool == null) return;
    form.setFieldValue('cash_pool', null);
    // And the rate the pool wrote — but ONLY where a pool could have written it
    // (dp review, Medium 1). `useBenchmarkRateSync` will not reclaim the field
    // on every exit - a switch to Rebate leaves rate_type at the 'fixed' fee
    // mode forced, and that branch bails - so without this the abandoned pool's
    // rate is left sitting in "Rebate Rate %" as if the trader typed it.
    //
    // The pool only ever writes the rate in fee mode (`useCashPoolRateSync`),
    // and a GMRA trade can never be in fee mode (`useRepoRateModeGuard`), so
    // "GMSLA" is exactly "the pool may have owned this field". Checking the mode
    // here instead would not do: on the fee -> rebate switch, the very exit this
    // guard exists for, the mode has ALREADY flipped by the time the effect
    // runs. A repo's "Repo Rate %" is typed by hand, so blanking it would leave
    // a blocking "Rate is required" on a field nothing refills.
    if (getAgreementType(tradeType as TradeType) !== 'gmra') {
      form.setFieldValue('benchmark_rate', '');
    }
  }, [poolApplies, cashPool, tradeType, form]);
}

/**
 * LR-631: a fee trade's benchmark IS its cash pool's rate, so the pool writes
 * both benchmark fields rather than the trader typing them — the Benchmark
 * control is hidden while a pool is selected.
 *
 * The same figure is what the backend stamps on the booking
 * (`PositionCreateSerializer._snapshot_pool_rate`), so the preview, the payload
 * and the row the nightly accrual prices all describe one rate. A fixed pool has
 * no index behind it and books under benchmark `other`, mirroring the server.
 *
 * A separate effect from `useRateModeReset` for the same reason
 * `useBenchmarkRateSync` is: the pool list and the benchmark registry both
 * arrive asynchronously, and this has to re-fire when they do.
 */
export function useCashPoolRateSync({
  form,
  rateEntryMode,
  poolRate,
  poolBenchmarkName,
}: {
  form: TradeForm;
  rateEntryMode: string | undefined;
  poolRate: number | undefined;
  poolBenchmarkName: string | undefined;
}) {
  useEffect(() => {
    if (rateEntryMode !== 'fee' || poolRate === undefined) return;
    form.setFieldValue('benchmark_name', (poolBenchmarkName || 'other') as BenchmarkRate);
    form.setFieldValue('benchmark_rate', poolRate.toString());
  }, [rateEntryMode, poolRate, poolBenchmarkName, form]);
}
